import { type PurchasedItineraryEntry } from "../store/persistence/slices/purchasedItineraries.slice";
import { type SavedItineraryEntry } from "../store/persistence/slices/savedItineraries.slice";
import { type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { itineraryLegEndpointDisplayLabel } from "./SbbChStationsSearchUrl.tools";
import { normalizeStopLabel } from "./StringTools";

/** Saved-list filter tab identifiers. */
export type SavedListFilterTabId = "all" | "itineraries" | "tickets";

/**
 * One row of the saved list: a user-saved (favorite) itinerary or a purchased one.
 *
 * @remarks `PurchasedItineraryEntry` is structurally a superset of
 * `SavedItineraryEntry` (`itinerary`, `savedAt`, route labels) plus a `purchase`
 * block — display helpers below read only the shared fields.
 */
export type SavedListEntry = SavedItineraryEntry | PurchasedItineraryEntry;

const ROUTE_LABEL_SEPARATOR = " → ";
const ITINERARY_DATE_LOCALE = "fr-CH";
const DEFAULT_ORIGIN_LABEL = "Départ";
const DEFAULT_DESTINATION_LABEL = "Arrivée";

/** Grace period for saved-itinerary temporal buckets (15 minutes). */
const SAVED_ITINERARY_TEMPORAL_GRACE_PERIOD_MS = 15 * 60 * 1000;

/**
 * Temporal section for a saved itinerary in the list UI.
 *
 * - `past` — after `endTime + grace`
 * - `current` — from `startTime - grace` until `endTime + grace` (inclusive)
 * - `upcoming` — before `startTime - grace`
 */
export type SavedItineraryTemporalSection = "past" | "current" | "upcoming";

/** Geocode-like item with one or more user-facing label fields. */
interface GeocodeLabelSource {
    "result"?:  string;
    "title"?:   string;
    "resume"?:  string;
    "address"?: string;
}

/**
 * Returns whether a leg endpoint label is a MOTIS routing placeholder.
 *
 * @param value Raw endpoint label from an itinerary leg.
 * @returns `true` when the label is empty or equals `START` / `END`.
 */
const isPlaceholderEndpointLabel = ( value: string | undefined ): boolean => {
    if ( value === undefined || value.trim() === "" ) {
        return true;
    }

    const normalized = value.trim().toUpperCase();

    return normalized === "START" || normalized === "END";
};

/**
 * Resolves a user-facing label from a geocode search selection.
 *
 * @param item Selected departure or arrival search item.
 * @returns Trimmed label when available.
 */
export const resolveGeocodeItemDisplayLabel = ( item: GeocodeLabelSource | null | undefined ): string | undefined => {
    if ( item === null || item === undefined ) {
        return undefined;
    }

    const candidates = [ item.result, item.title, item.resume, item.address ];
    for ( const candidate of candidates ) {
        const trimmed = candidate?.trim();
        if ( trimmed !== undefined && trimmed.length > 0 ) {
            return trimmed;
        }
    }

    return undefined;
};

/**
 * Parses an itinerary departure timestamp to epoch milliseconds.
 *
 * @param itinerary Itinerary whose `startTime` is parsed.
 * @returns Departure time in ms, or `0` when parsing fails.
 */
export const resolveItineraryDepartureMs = ( itinerary: ItineraryDTO ): number => {
    const departureMs = Date.parse( itinerary.startTime );

    return Number.isFinite( departureMs ) ? departureMs : 0;
};

/**
 * Parses an itinerary arrival timestamp to epoch milliseconds.
 *
 * @param itinerary Itinerary whose `endTime` is parsed.
 * @returns Arrival time in ms, or `0` when parsing fails.
 */
export const resolveItineraryEndMs = ( itinerary: ItineraryDTO ): number => {
    const endMs = Date.parse( itinerary.endTime );

    return Number.isFinite( endMs ) ? endMs : 0;
};

/**
 * Parses departure time for a persisted saved entry.
 *
 * @param entry Saved itinerary entry.
 * @returns Departure time in ms.
 */
export const resolveSavedItineraryDepartureMs = ( entry: SavedListEntry ): number => {
    return resolveItineraryDepartureMs( entry.itinerary );
};

/**
 * Parses arrival time for a persisted saved entry.
 *
 * @param entry Saved itinerary entry.
 * @returns Arrival time in ms.
 */
export const resolveSavedItineraryEndMs = ( entry: SavedListEntry ): number => {
    return resolveItineraryEndMs( entry.itinerary );
};

/**
 * Returns whether a saved itinerary is not yet finished (upcoming or in progress).
 *
 * Past status is based on {@link ItineraryDTO.endTime} plus the shared temporal grace
 * period, not on departure time.
 *
 * @param entry Saved itinerary entry.
 * @param nowMs Reference clock (defaults to `Date.now()`).
 * @returns `true` when `endTime + grace period >= now`.
 */
export const isSavedItineraryNotPast = (
    entry: SavedListEntry,
    nowMs: number = Date.now(),
): boolean => {
    return resolveSavedItineraryEndMs( entry ) + SAVED_ITINERARY_TEMPORAL_GRACE_PERIOD_MS >= nowMs;
};

/**
 * Returns whether a saved itinerary is in the "current" temporal window.
 *
 * Current means not past, and `now` has reached `startTime - grace` (same grace
 * duration as the post-arrival past threshold).
 *
 * @param entry Saved itinerary entry.
 * @param nowMs Reference clock (defaults to `Date.now()`).
 * @returns `true` when the itinerary belongs in the Actuellement section.
 */
export const isSavedItineraryCurrent = (
    entry: SavedListEntry,
    nowMs: number = Date.now(),
): boolean => {
    if ( !isSavedItineraryNotPast( entry, nowMs ) ) {
        return false;
    }

    const currentWindowStartMs = resolveSavedItineraryDepartureMs( entry ) - SAVED_ITINERARY_TEMPORAL_GRACE_PERIOD_MS;

    return nowMs >= currentWindowStartMs;
};

/**
 * Returns whether a saved itinerary is still strictly upcoming (before the current window).
 *
 * @param entry Saved itinerary entry.
 * @param nowMs Reference clock (defaults to `Date.now()`).
 * @returns `true` when `now < startTime - grace` and the itinerary is not past.
 */
export const isSavedItineraryUpcoming = (
    entry: SavedListEntry,
    nowMs: number = Date.now(),
): boolean => {
    return isSavedItineraryNotPast( entry, nowMs ) && !isSavedItineraryCurrent( entry, nowMs );
};

/**
 * Resolves which saved-list temporal section an entry belongs to.
 *
 * @param entry Saved itinerary entry.
 * @param nowMs Reference clock (defaults to `Date.now()`).
 * @returns `past`, `current`, or `upcoming`.
 */
export const resolveSavedItineraryTemporalSection = (
    entry: SavedListEntry,
    nowMs: number = Date.now(),
): SavedItineraryTemporalSection => {
    if ( !isSavedItineraryNotPast( entry, nowMs ) ) {
        return "past";
    }

    if ( isSavedItineraryCurrent( entry, nowMs ) ) {
        return "current";
    }

    return "upcoming";
};

/**
 * Counts saved itineraries that are not yet finished.
 *
 * @param entries Saved itinerary entries.
 * @param nowMs Reference clock (defaults to `Date.now()`).
 * @returns Number of entries still within the arrival grace window.
 */
export const countFutureSavedItineraries = (
    entries: SavedListEntry[],
    nowMs: number = Date.now(),
): number => {
    return entries.reduce( ( count, entry ) => {
        return count + ( isSavedItineraryNotPast( entry, nowMs ) ? 1 : 0 );
    }, 0 );
};

/**
 * Counts saved itineraries whose arrival is strictly in the past.
 *
 * @param entries Saved itinerary entries.
 * @param nowMs Reference clock (defaults to `Date.now()`).
 * @returns Number of entries with arrival plus grace period strictly before `now`.
 */
export const countPastSavedItineraries = (
    entries: SavedListEntry[],
    nowMs: number = Date.now(),
): number => {
    return entries.reduce( ( count, entry ) => {
        return count + ( isSavedItineraryNotPast( entry, nowMs ) ? 0 : 1 );
    }, 0 );
};

/**
 * Sorts saved entries by departure time ascending (earliest first).
 *
 * @param entries Saved itinerary entries.
 * @returns New array sorted by `startTime`.
 */
export const sortSavedItinerariesByDeparture = ( entries: SavedListEntry[] ): SavedListEntry[] => {
    return [ ...entries ].sort( ( left, right ) => {
        return resolveSavedItineraryDepartureMs( left ) - resolveSavedItineraryDepartureMs( right );
    } );
};

/**
 * Finds the first meaningful endpoint label scanning legs forward.
 *
 * @param itinerary Itinerary snapshot.
 * @returns Departure-side label or empty string.
 */
const resolveItineraryOriginLabelFromLegs = ( itinerary: ItineraryDTO ): string => {
    for ( const leg of itinerary.legs ) {
        const fromLabel = itineraryLegEndpointDisplayLabel( leg.from );
        if ( !isPlaceholderEndpointLabel( fromLabel ) ) {
            return fromLabel;
        }

        const toLabel = itineraryLegEndpointDisplayLabel( leg.to );
        if ( !isPlaceholderEndpointLabel( toLabel ) ) {
            return toLabel;
        }
    }

    return "";
};

/**
 * Finds the last meaningful endpoint label scanning legs backward.
 *
 * @param itinerary Itinerary snapshot.
 * @returns Arrival-side label or empty string.
 */
const resolveItineraryDestinationLabelFromLegs = ( itinerary: ItineraryDTO ): string => {
    for ( let index = itinerary.legs.length - 1; index >= 0; index -= 1 ) {
        const leg = itinerary.legs[index];
        const toLabel = itineraryLegEndpointDisplayLabel( leg?.to );
        if ( !isPlaceholderEndpointLabel( toLabel ) ) {
            return toLabel;
        }

        const fromLabel = itineraryLegEndpointDisplayLabel( leg?.from );
        if ( !isPlaceholderEndpointLabel( fromLabel ) ) {
            return fromLabel;
        }
    }

    return "";
};

/**
 * Resolves the itinerary departure endpoint label (first leg origin).
 *
 * @param itinerary Itinerary snapshot.
 * @returns Departure place name or default label.
 */
export const resolveItineraryOriginLabel = ( itinerary: ItineraryDTO ): string => {
    const fromLegLabel = itineraryLegEndpointDisplayLabel( itinerary.legs[0]?.from );
    if ( !isPlaceholderEndpointLabel( fromLegLabel ) ) {
        return fromLegLabel;
    }

    const scannedLabel = resolveItineraryOriginLabelFromLegs( itinerary );
    if ( scannedLabel.length > 0 ) {
        return scannedLabel;
    }

    const normalizedPlaceholder = normalizeStopLabel( fromLegLabel );
    if ( normalizedPlaceholder !== undefined ) {
        return normalizedPlaceholder;
    }

    return DEFAULT_ORIGIN_LABEL;
};

/**
 * Resolves the itinerary arrival endpoint label (last leg destination).
 *
 * @param itinerary Itinerary snapshot.
 * @returns Arrival place name or default label.
 */
export const resolveItineraryDestinationLabel = ( itinerary: ItineraryDTO ): string => {
    const lastLeg = itinerary.legs[itinerary.legs.length - 1];
    const toLegLabel = itineraryLegEndpointDisplayLabel( lastLeg?.to );
    if ( !isPlaceholderEndpointLabel( toLegLabel ) ) {
        return toLegLabel;
    }

    const scannedLabel = resolveItineraryDestinationLabelFromLegs( itinerary );
    if ( scannedLabel.length > 0 ) {
        return scannedLabel;
    }

    const normalizedPlaceholder = normalizeStopLabel( toLegLabel );
    if ( normalizedPlaceholder !== undefined ) {
        return normalizedPlaceholder;
    }

    return DEFAULT_DESTINATION_LABEL;
};

/**
 * Resolves the departure label for a saved itinerary entry.
 *
 * @param entry Persisted saved entry.
 * @returns User-facing departure label.
 */
export const resolveSavedItineraryOriginLabel = ( entry: SavedListEntry ): string => {
    const persistedLabel = entry.originLabel?.trim();
    if ( persistedLabel !== undefined && persistedLabel.length > 0 ) {
        return persistedLabel;
    }

    return resolveItineraryOriginLabel( entry.itinerary );
};

/**
 * Resolves the arrival label for a saved itinerary entry.
 *
 * @param entry Persisted saved entry.
 * @returns User-facing arrival label.
 */
export const resolveSavedItineraryDestinationLabel = ( entry: SavedListEntry ): string => {
    const persistedLabel = entry.destinationLabel?.trim();
    if ( persistedLabel !== undefined && persistedLabel.length > 0 ) {
        return persistedLabel;
    }

    return resolveItineraryDestinationLabel( entry.itinerary );
};

/**
 * Resolves a human-readable route label from departure and arrival endpoint names.
 *
 * @param itinerary Itinerary snapshot.
 * @returns `"Départ → Arrivée"` or a fallback when names are missing.
 */
export const resolveItineraryRouteLabel = ( itinerary: ItineraryDTO ): string => {
    const originName = resolveItineraryOriginLabel( itinerary );
    const destinationName = resolveItineraryDestinationLabel( itinerary );

    if ( originName.length > 0 && destinationName.length > 0 ) {
        return `${ originName }${ ROUTE_LABEL_SEPARATOR }${ destinationName }`;
    }

    if ( originName.length > 0 ) {
        return originName;
    }

    if ( destinationName.length > 0 ) {
        return destinationName;
    }

    return "Itinéraire sauvegardé";
};

/**
 * Formats the itinerary departure date for saved-list cards.
 *
 * @param itinerary Itinerary snapshot.
 * @returns Localized date string (fr-CH).
 */
export const resolveItineraryDateLabel = ( itinerary: ItineraryDTO ): string => {
    const departureMs = resolveItineraryDepartureMs( itinerary );
    if ( departureMs <= 0 ) {
        return "";
    }

    return new Date( departureMs ).toLocaleDateString( ITINERARY_DATE_LOCALE, {
        "weekday": "short",
        "day":     "numeric",
        "month":   "short",
        "year":    "numeric",
    } );
};

/**
 * Applies temporal filtering to saved-list entries.
 *
 * @param entries Saved-list entries (favorites and/or purchased).
 * @param options Filter options for temporal visibility.
 * @returns Saved entries sorted by departure time.
 */
export const prepareSavedItineraryEntriesForDisplay = (
    entries: SavedListEntry[],
    options: {
        "showPast": boolean;
        "nowMs"?:   number;
    },
): SavedListEntry[] => {
    const nowMs = options.nowMs ?? Date.now();
    const sortedEntries = sortSavedItinerariesByDeparture( entries );

    return sortedEntries.filter( ( entry ) => {
        if ( options.showPast ) {
            return true;
        }

        return isSavedItineraryNotPast( entry, nowMs );
    } );
};

/**
 * Applies temporal filtering, then returns display-ready itineraries.
 *
 * @param entries Saved-list entries (favorites and/or purchased).
 * @param options Filter options for temporal visibility.
 * @returns Itinerary DTOs sorted by departure time.
 */
export const prepareSavedItinerariesForDisplay = (
    entries: SavedListEntry[],
    options: {
        "showPast": boolean;
        "nowMs"?:   number;
    },
): ItineraryDTO[] => {
    return prepareSavedItineraryEntriesForDisplay( entries, options ).
        map( ( entry ) => entry.itinerary );
};
